import React, { useEffect, useState } from 'react'

import { useBottomScrollListener } from 'react-bottom-scroll-listener'
import { useTranslation } from 'react-i18next'

import ControlArea from '@components/myScores/ControlArea'
import RemoveSharedScoreButton from '@components/myScores/ControlArea/RemoveSharedScoreButton'
import ScoresView from '@components/myScores/ScoresView'
import { ScoreViewOptions } from '@components/myScores/utils/types'
import LoadingSpinner from '@components/shared/LoadingSpinner'
import NonIdealState from '@components/shared/NonIdealState'
import {
  SHARED_SCORES_PAGE_SIZE,
  SharedScoresResponse,
} from '@pages/home/loaders/sharedScores.loader'
import SelectionProvider from '@providers/SelectionProvider'
import ApiRoutes from '@utils/api-routes'
import { ViewType } from '@utils/global'
import http from '@utils/http'
import Storage from '@utils/storage'

interface Props {
  initialData: SharedScoresResponse
  order: string | null
}

const SharedScoresView: React.FC<Props> = ({ initialData, order }) => {
  const { t } = useTranslation()

  const [loading, setLoading] = useState(false)
  const [scoresData, setScoresData] = useState(initialData)
  const [layoutType, setLayoutType] = useState(Storage.scoresViewType)
  const setViewType = (type: ViewType): void => {
    setLayoutType(type)
    Storage.scoresViewType = type
  }
  const { scores, remaining, startIndex } = scoresData

  const scrollRef = useBottomScrollListener<HTMLDivElement>(
    async () => {
      if (!remaining || loading) return

      setLoading(true)
      const [newResp] = await http.post<SharedScoresResponse>(
        ApiRoutes.members.sharedScoresPath(),
        {
          order,
          max_count: SHARED_SCORES_PAGE_SIZE,
          start_index: startIndex,
        }
      )

      if (newResp?.data) {
        setScoresData((prevData) => ({
          ...newResp.data,
          scores: [...prevData.scores, ...newResp.data.scores],
        }))
      }
      setLoading(false)
    },
    {
      triggerOnNoScroll: true,
      offset: 32,
    }
  )

  // Reset the data when we get something new loaded
  useEffect(() => {
    setScoresData(initialData)
  }, [initialData])

  if (!scores.length) {
    return (
      <NonIdealState
        icon="people"
        title={t('content.empty.shared.title')}
        subtitle={t('content.empty.shared.subtitle')}
      />
    )
  }

  const options: ScoreViewOptions = {
    showAuthor: true,
    hideState: true,
  }

  return (
    <SelectionProvider initialItems={scores}>
      <ControlArea setViewType={setViewType} viewType={layoutType} order={order}>
        <RemoveSharedScoreButton />
      </ControlArea>
      <div className="flex-1 overflow-y-auto relative pb-4" ref={scrollRef}>
        <ScoresView scores={scores} viewType={layoutType} options={options} />
        {remaining && loading && <LoadingSpinner />}
      </div>
    </SelectionProvider>
  )
}

export default SharedScoresView
